import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for managing file upload functionality
 * @returns {Object} File upload state and functions
 */
export const useFileUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  /**
   * Handle file selection from input
   * @param {Event} event - The file input change event
   */
  const handleFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;
    
    // Filter for image files only (as per the original accept="image/*")
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      console.warn('Some files were not images and were filtered out');
    }
    
    // Create file objects with preview URLs
    const newFiles = imageFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      previewUrl: URL.createObjectURL(file)
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    
    // Clear the input value to allow re-uploading the same file
    if (event.target) {
      event.target.value = '';
    }
  }, []);

  /**
   * Remove a file from the upload list
   * @param {number} index - The index of the file to remove
   */
  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      const removedFile = newFiles[index];
      
      // Revoke the preview URL to free memory
      if (removedFile?.previewUrl) {
        URL.revokeObjectURL(removedFile.previewUrl);
      }
      
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  /**
   * Remove all uploaded files
   */
  const clearAllFiles = useCallback(() => {
    // Revoke all preview URLs to free memory
    uploadedFiles.forEach(file => {
      if (file?.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    
    setUploadedFiles([]);
  }, [uploadedFiles]);

  /**
   * Open the file picker dialog
   */
  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  /**
   * Get the total size of all uploaded files
   * @returns {number} Total size in bytes
   */
  const getTotalFileSize = useCallback(() => {
    return uploadedFiles.reduce((total, file) => total + (file.size || 0), 0);
  }, [uploadedFiles]);

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * Validate if a file can be uploaded
   * @param {File} file - The file to validate
   * @returns {Object} Validation result with isValid and error message
   */
  const validateFile = useCallback((file) => {
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      return { isValid: false, error: 'Only image files are allowed' };
    }
    
    // Check file size (e.g., 10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'File size must be less than 10MB' };
    }
    
    return { isValid: true, error: null };
  }, []);

  /**
   * Get the file objects for upload
   * @returns {Array} Array of File objects
   */
  const getFilesForUpload = useCallback(() => {
    return uploadedFiles.map(fileData => fileData.file);
  }, [uploadedFiles]);

  return {
    uploadedFiles,
    setUploadedFiles,
    fileInputRef,
    handleFileUpload,
    removeFile,
    clearAllFiles,
    openFilePicker,
    getTotalFileSize,
    formatFileSize,
    validateFile,
    getFilesForUpload
  };
};